import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { StripeCheckout } from '@stripe/stripe-js';
import OrderBreakdown from '../../components/funnel/OrderBreakdown';
import HelpBox from '../../components/funnel/HelpBox';
import FunnelShell from './FunnelShell';
import { useFunnelCart } from '../../context/FunnelCartProvider';
import { apiFetch } from '../../lib/api';
import {
  filterCheckoutLaneSelectionsByTierSkuIds,
  writeCheckoutLaneSelections,
  type CheckoutLaneSelection,
} from '../../lib/funnel/checkoutLaneSelections';
import { parseSharedCheckoutToken } from '../../lib/funnel/shareCheckoutLink';
import { stripePromise } from '../../lib/stripePublic';
import type { CalculatedOrder } from '../../lib/pricing/calcOrder';

const Layout = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: minmax(0, 1fr) 320px;
  min-height: calc(100svh - (72px + env(safe-area-inset-top)) - 3rem);
  align-content: start;

  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const MainColumn = styled.section`
  width: min(100%, 760px);
  margin: 0 auto;
  display: grid;
  gap: 1rem;
`;

const AnimatedSection = styled.div<{ $visible: boolean }>`
  max-height: ${({ $visible }) => ($visible ? '2400px' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-8px')});
  overflow: hidden;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    max-height 280ms ease,
    opacity 220ms ease,
    transform 220ms ease;
`;

const Heading = styled.h1`
  margin: 0;
  font-size: clamp(2rem, 3.6vw, 2.6rem);
`;

const Accordion = styled.section`
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  padding-top: 0.95rem;
  display: grid;
  gap: 0.9rem;
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const AccordionTitleBlock = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const AccordionTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
`;

const AccordionDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.92rem;
`;

const EditButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ContactForm = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const ContactRow = styled.div`
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ContactField = styled.label`
  display: grid;
  gap: 0.4rem;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.9rem;
`;

const ContactInput = styled.input<{ $invalid?: boolean }>`
  border: 1px solid
    ${({ $invalid, theme }) => ($invalid ? theme.colors.danger : theme.colors.gray200)};
  border-radius: 10px;
  padding: 0.7rem 0.75rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray900};

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    opacity: 0.72;
  }
`;

const ContactTextArea = styled.textarea`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  padding: 0.7rem 0.75rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray900};
  min-height: 110px;
  resize: vertical;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    opacity: 0.72;
  }
`;

const ContactFieldError = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.8rem;
  line-height: 1.2;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SubtleActionButton = styled.button`
  border: 0;
  background: transparent;
  color: #e0a15e;
  border-radius: 8px;
  padding: 0.25rem 0;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    color: #e0a15e;
    background: transparent;
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.danger};
`;

const InlineSummary = styled.div`
  display: grid;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: 0.9rem;
`;

const DiscountRow = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const DiscountInputRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: nowrap;
  align-items: center;

  @media (max-width: 760px) {
    flex-wrap: wrap;
  }
`;

const DiscountInput = styled.input`
  min-width: 0;
  flex: 1 1 260px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray900};

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray700};
    opacity: 0.72;
  }
`;

const DiscountMeta = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.84rem;
`;

const BillingNote = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.9rem;
`;

const PaymentSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  padding-top: 0.85rem;
  display: grid;
  gap: 0.85rem;
`;

const PaymentMount = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.gray200};
  border-radius: 12px;
  padding: 0.85rem;
  background: transparent;
`;

const SideColumn = styled(AnimatedSection)`
  display: grid;
  gap: 1rem;
  align-content: start;
`;

type ContactFormState = {
  firstName: string;
  lastName: string;
  phone: string;
  bio: string;
  email: string;
  confirmEmail: string;
};

type CaptureLeadResponse = {
  leadCaptureId: string;
};

type CreateCheckoutSessionResponse = {
  checkoutSessionId: string;
  uiMode: 'hosted' | 'embedded' | 'custom';
  checkoutUrl: string | null;
  clientSecret: string | null;
};

type CheckoutQuoteResponse = {
  order: CalculatedOrder;
  appliedDiscountCode: string | null;
};

type RecoverCheckoutResponse = {
  leadCaptureId: string;
  contact: {
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    bio: string;
  };
  cartItems: Array<{ skuId: string; quantity: number }>;
  laneSelections: CheckoutLaneSelection[];
  discountCode: string | null;
};

type CheckoutDraftState = {
  leadCaptureId: string;
  contact: ContactFormState;
  contactComplete: boolean;
  discountCode: string | null;
};

type SharedCheckoutSummary = {
  serviceName: string;
  tierName: string;
  laneLabels: string[];
  addOnLabels: string[];
};

const CHECKOUT_DRAFT_STORAGE_KEY = 'cybard.checkout.draft.v1';

function validateContact(input: ContactFormState): string | null {
  if (!input.firstName.trim()) return 'First name is required.';
  if (!input.lastName.trim()) return 'Last name is required.';
  if (!input.email.trim()) return 'Email is required.';
  if (!input.confirmEmail.trim()) return 'Confirm email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
    return 'Enter a valid email address.';
  }
  if (input.email.trim().toLowerCase() !== input.confirmEmail.trim().toLowerCase()) {
    return 'Email and confirm email must match.';
  }
  return null;
}

function buildContactPayload(input: ContactFormState) {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    firstName,
    lastName,
    fullName,
    customerType: 'individual' as const,
    businessName: '',
    industry: '',
    businessAddress: '',
    businessPhone: '',
    businessWebsite: '',
    phone: input.phone.trim(),
    bio: input.bio.trim(),
    email: input.email.trim(),
  };
}

function buildCheckoutLaneSelections(
  items: ReadonlyArray<{ skuId: string; quantity: number }>,
): CheckoutLaneSelection[] {
  const tierSkuIds = items.map((item) => item.skuId);
  return filterCheckoutLaneSelectionsByTierSkuIds(tierSkuIds);
}

function getEmailMismatchError(email: string, confirmEmail: string): string | null {
  if (!email.trim() || !confirmEmail.trim()) return null;
  if (email.trim().toLowerCase() === confirmEmail.trim().toLowerCase()) return null;
  return 'Email and confirm email must match.';
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { order, items, replaceItems, removeSku } = useFunnelCart();
  const [form, setForm] = useState<ContactFormState>({
    firstName: '',
    lastName: '',
    phone: '',
    bio: '',
    email: '',
    confirmEmail: '',
  });
  const [leadCaptureId, setLeadCaptureId] = useState<string | null>(null);
  const [contactComplete, setContactComplete] = useState(false);
  const [showContactForm, setShowContactForm] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [capturingLead, setCapturingLead] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string | null>(null);
  const [quotedOrder, setQuotedOrder] = useState<CalculatedOrder | null>(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const [checkoutActionsReady, setCheckoutActionsReady] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [recoveringCheckout, setRecoveringCheckout] = useState(false);
  const [sharedCheckoutSummary, setSharedCheckoutSummary] = useState<SharedCheckoutSummary | null>(
    null,
  );
  const checkoutMountRef = useRef<HTMLDivElement | null>(null);
  const checkoutSdkRef = useRef<StripeCheckout | null>(null);
  const checkoutConfirmRef = useRef<null | ((args?: any) => Promise<any>)>(null);
  const recoverTokenRef = useRef<string>(
    new URLSearchParams(window.location.search).get('recover') ?? '',
  );
  const shareTokenRef = useRef<string>(
    new URLSearchParams(window.location.search).get('share') ?? '',
  );
  const abandonQueuedRef = useRef(false);
  const skipAbandonRef = useRef(false);

  const canCheckout = order.lineItems.length > 0;
  const contactError = useMemo(() => validateContact(form), [form]);
  const emailMismatchError = useMemo(
    () => getEmailMismatchError(form.email, form.confirmEmail),
    [form.confirmEmail, form.email],
  );
  const displayOrder = quotedOrder ?? order;
  const resetCustomCheckout = useCallback(() => {
    checkoutConfirmRef.current = null;
    checkoutSdkRef.current = null;
    setCheckoutActionsReady(false);
    setCheckoutClientSecret(null);
    setCheckoutSessionId(null);
    setConfirmingPayment(false);
  }, []);

  const queueAbandonCheckout = useCallback(
    (reason: string, preferBeacon: boolean) => {
      if (!leadCaptureId || abandonQueuedRef.current || skipAbandonRef.current) return;
      if (confirmingPayment) return;
      abandonQueuedRef.current = true;
      const payload = JSON.stringify({
        leadCaptureId,
        reason,
      });
      const apiBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
      const url = `${apiBase}/api/funnel/checkout/abandon`;

      try {
        if (preferBeacon && typeof navigator.sendBeacon === 'function') {
          const beaconBody = new Blob([payload], { type: 'application/json' });
          const queued = navigator.sendBeacon(url, beaconBody);
          if (queued) return;
        }
      } catch {
        // Fall through to fetch keepalive.
      }

      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // Best-effort abandon tracking.
      });
    },
    [confirmingPayment, leadCaptureId],
  );

  const touchCheckoutActivity = useCallback(async () => {
    if (!leadCaptureId) return;
    try {
      await apiFetch('/api/funnel/checkout/activity', {
        method: 'POST',
        body: JSON.stringify({
          leadCaptureId,
          stage:
            contactComplete && !showContactForm
              ? 'review_billing'
              : 'contact',
        }),
      });
    } catch {
      // Best-effort heartbeat.
    }
  }, [contactComplete, leadCaptureId, showContactForm]);

  const continueShopping = useCallback(() => {
    skipAbandonRef.current = true;
    navigate('/services');
  }, [navigate]);

  const removeItemFromCheckout = useCallback(
    (skuId: string) => {
      resetCustomCheckout();
      setQuotedOrder(null);
      setAppliedDiscountCode(null);
      setError(null);
      removeSku(skuId);
    },
    [removeSku, resetCustomCheckout],
  );
  const captureLead = useCallback(async (): Promise<boolean> => {
    if (leadCaptureId) return true;

    const validation = validateContact(form);
    if (validation) {
      setError(validation);
      return false;
    }

    setCapturingLead(true);
    setError(null);
    try {
      const response = await apiFetch<CaptureLeadResponse>('/api/funnel/lead', {
        method: 'POST',
        body: JSON.stringify({
          cartItems: items,
          laneSelections: buildCheckoutLaneSelections(items),
          contact: buildContactPayload(form),
        }),
      });
      setLeadCaptureId(response.leadCaptureId);
      return true;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save your contact details.',
      );
      return false;
    } finally {
      setCapturingLead(false);
    }
  }, [form, items, leadCaptureId]);

  const saveContactAndContinue = useCallback(async () => {
    const validation = validateContact(form);
    if (validation) {
      setError(validation);
      return;
    }
    const ok = await captureLead();
    if (!ok) return;
    setContactComplete(true);
    setShowContactForm(false);
    setError(null);
  }, [captureLead, form]);

  const applyDiscountCode = useCallback(async () => {
    const code = discountCodeInput.trim();
    if (!code) {
      setError('Enter a discount code.');
      return;
    }
    if (!items.length) {
      setError('Add at least one service before applying a discount.');
      return;
    }

    setApplyingDiscount(true);
    setError(null);
    resetCustomCheckout();
    try {
      const response = await apiFetch<CheckoutQuoteResponse>('/api/funnel/checkout/quote', {
        method: 'POST',
        body: JSON.stringify({
          cartItems: items,
          discountCode: code,
        }),
      });
      setQuotedOrder(response.order);
      setAppliedDiscountCode(response.appliedDiscountCode);
      setDiscountCodeInput(response.appliedDiscountCode ?? code.toUpperCase());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to apply discount code.');
      setQuotedOrder(null);
      setAppliedDiscountCode(null);
    } finally {
      setApplyingDiscount(false);
    }
  }, [discountCodeInput, items, resetCustomCheckout]);

  const clearDiscountCode = useCallback(() => {
    setDiscountCodeInput('');
    setAppliedDiscountCode(null);
    setQuotedOrder(null);
    resetCustomCheckout();
    setError(null);
  }, [resetCustomCheckout]);

  useEffect(() => {
    if (recoverTokenRef.current) return;
    const token = shareTokenRef.current;
    if (!token) return;

    const sharedSelection = parseSharedCheckoutToken(token);
    if (!sharedSelection) {
      setError('This shared checkout link is invalid.');
      shareTokenRef.current = '';
      return;
    }

    replaceItems(sharedSelection.cartItems);
    setLeadCaptureId(null);
    setContactComplete(false);
    setShowContactForm(true);
    setDiscountCodeInput('');
    setAppliedDiscountCode(null);
    setQuotedOrder(null);
    setSharedCheckoutSummary({
      serviceName: sharedSelection.serviceName,
      tierName: sharedSelection.tierName,
      laneLabels: sharedSelection.laneLabels ? [...sharedSelection.laneLabels] : [],
      addOnLabels: sharedSelection.addOnLabels ? [...sharedSelection.addOnLabels] : [],
    });
    writeCheckoutLaneSelections(
      sharedSelection.tierSkuId
        ? [
            {
              tierSkuId: sharedSelection.tierSkuId,
              laneIds: sharedSelection.laneIds ? [...sharedSelection.laneIds] : [],
              laneLabels: sharedSelection.laneLabels ? [...sharedSelection.laneLabels] : [],
            },
          ]
        : [],
    );
    setError(null);
    resetCustomCheckout();
    window.localStorage.removeItem(CHECKOUT_DRAFT_STORAGE_KEY);

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('share');
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    shareTokenRef.current = '';
  }, [replaceItems, resetCustomCheckout]);

  useEffect(() => {
    if (recoverTokenRef.current || shareTokenRef.current) return;
    try {
      const raw = window.localStorage.getItem(CHECKOUT_DRAFT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<CheckoutDraftState>;
      if (!parsed || typeof parsed !== 'object') return;
      const draftLeadCaptureId = String(parsed.leadCaptureId ?? '').trim();
      if (!draftLeadCaptureId) return;
      const draftContact = parsed.contact;
      if (!draftContact || typeof draftContact !== 'object') return;

      setForm({
        email: String(draftContact.email ?? ''),
        confirmEmail: String(draftContact.confirmEmail ?? ''),
        firstName: String(draftContact.firstName ?? ''),
        lastName: String(draftContact.lastName ?? ''),
        phone: String(draftContact.phone ?? ''),
        bio: String(draftContact.bio ?? ''),
      });
      setLeadCaptureId(draftLeadCaptureId);
      const isContactComplete = Boolean(parsed.contactComplete);
      setContactComplete(isContactComplete);
      setShowContactForm(!isContactComplete);
      const draftDiscountCode = String(parsed.discountCode ?? '').trim();
      setDiscountCodeInput(draftDiscountCode);
      setAppliedDiscountCode(null);
      setQuotedOrder(null);
      resetCustomCheckout();
    } catch {
      // Ignore malformed local draft and continue checkout normally.
    }
  }, [resetCustomCheckout]);

  useEffect(() => {
    if (!leadCaptureId) {
      window.localStorage.removeItem(CHECKOUT_DRAFT_STORAGE_KEY);
      return;
    }

    const draft: CheckoutDraftState = {
      leadCaptureId,
      contact: form,
      contactComplete,
      discountCode: discountCodeInput.trim() || null,
    };
    window.localStorage.setItem(CHECKOUT_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  }, [contactComplete, discountCodeInput, form, leadCaptureId]);

  useEffect(() => {
    const token = recoverTokenRef.current;
    if (!token) return;
    let cancelled = false;

    const hydrateRecovery = async () => {
      setRecoveringCheckout(true);
      setError(null);
      try {
        const response = await apiFetch<RecoverCheckoutResponse>(
          `/api/funnel/checkout/recover/${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        replaceItems(response.cartItems);
        setForm({
          email: response.contact.email || '',
          confirmEmail: response.contact.email || '',
          firstName: response.contact.firstName || '',
          lastName: response.contact.lastName || '',
          phone: response.contact.phone || '',
          bio: response.contact.bio || '',
        });
        setLeadCaptureId(response.leadCaptureId);
        setContactComplete(true);
        setShowContactForm(false);
        setDiscountCodeInput(response.discountCode ?? '');
        setAppliedDiscountCode(null);
        setQuotedOrder(null);
        writeCheckoutLaneSelections(response.laneSelections ?? []);
        resetCustomCheckout();

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.delete('recover');
        window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to recover your saved checkout right now.',
        );
      } finally {
        if (!cancelled) {
          setRecoveringCheckout(false);
        }
      }
    };

    void hydrateRecovery();

    return () => {
      cancelled = true;
    };
  }, [replaceItems, resetCustomCheckout]);

  useEffect(() => {
    if (!leadCaptureId || confirmingPayment) return;
    void touchCheckoutActivity();
    const intervalId = window.setInterval(() => {
      void touchCheckoutActivity();
    }, 30_000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [confirmingPayment, leadCaptureId, touchCheckoutActivity]);

  useEffect(() => {
    const onPageHide = () => {
      queueAbandonCheckout('page_hide', true);
    };

    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      queueAbandonCheckout('route_leave', false);
    };
  }, [queueAbandonCheckout]);

  useEffect(() => {
    if (!items.length) {
      resetCustomCheckout();
      setQuotedOrder(null);
      setAppliedDiscountCode(null);
    }
  }, [items, resetCustomCheckout]);

  useEffect(() => {
    if (!checkoutClientSecret || !checkoutMountRef.current) {
      return;
    }

    let isMounted = true;
    let mountedPaymentElement: { unmount?: () => void; destroy?: () => void } | null = null;

    const initializeCustomCheckout = async () => {
      setCheckoutActionsReady(false);
      setError(null);
      try {
        if (!stripePromise) {
          throw new Error('Stripe is unavailable. Check publishable key configuration.');
        }
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe failed to initialize.');
        }

        const checkout = stripe.initCheckout({
          clientSecret: checkoutClientSecret,
          elementsOptions: {
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#e0a15e',
                colorBackground: '#111827',
                colorText: '#f3f4f6',
                colorDanger: '#ef4444',
                borderRadius: '10px',
                spacingUnit: '4px',
              },
              rules: {
                '.Input': {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.22)',
                  boxShadow: 'none',
                },
                '.Input:focus': {
                  border: '1px solid rgba(224,161,94,0.7)',
                  boxShadow: '0 0 0 1px rgba(224,161,94,0.28)',
                },
                '.Label': {
                  color: '#f3f4f6',
                  fontWeight: '600',
                },
              },
            },
          },
        });

        if (!isMounted || !checkoutMountRef.current) return;

        checkoutSdkRef.current = checkout;
        const paymentElement = checkout.createPaymentElement({
          layout: 'tabs',
          paymentMethodOrder: ['card'],
          terms: { card: 'never' },
        });
        mountedPaymentElement = paymentElement;
        paymentElement.mount(checkoutMountRef.current);

        const loaded = await checkout.loadActions();
        if (!isMounted) return;
        if (loaded.type === 'error') {
          throw new Error(loaded.error.message || 'Unable to load checkout actions.');
        }

        checkoutConfirmRef.current = loaded.actions.confirm;
        setCheckoutActionsReady(true);
      } catch (err) {
        if (!isMounted) return;
        setError(
          err instanceof Error ? err.message : 'Unable to load payment form right now.',
        );
        checkoutConfirmRef.current = null;
        setCheckoutActionsReady(false);
      }
    };

    void initializeCustomCheckout();

    return () => {
      isMounted = false;
      checkoutConfirmRef.current = null;
      checkoutSdkRef.current = null;
      setCheckoutActionsReady(false);
      try {
        mountedPaymentElement?.unmount?.();
        mountedPaymentElement?.destroy?.();
      } catch {
        // Ignore Stripe element teardown errors during fast remounts.
      }
    };
  }, [checkoutClientSecret]);

  const beginPayment = useCallback(async () => {
    if (!canCheckout) {
      setError('Add at least one service before checkout.');
      return;
    }
    if (!contactComplete) {
      setError('Complete your contact details before payment.');
      setShowContactForm(true);
      return;
    }
    if (!leadCaptureId) {
      setError('Contact details were not saved. Please save and continue again.');
      setShowContactForm(true);
      return;
    }

    const validation = validateContact(form);
    if (validation) {
      setError(validation);
      setShowContactForm(true);
      return;
    }
    if (discountCodeInput.trim() && !appliedDiscountCode) {
      setError('Apply your discount code before checkout, or clear it.');
      return;
    }

    resetCustomCheckout();
    setSubmitting(true);
    setError(null);
    try {
      const baseUrl = window.location.origin;
      const requestedUiMode: 'custom' | 'hosted' = stripePromise ? 'custom' : 'hosted';
      const payload = {
        leadCaptureId,
        cartItems: items,
        laneSelections: buildCheckoutLaneSelections(items),
        discountCode: appliedDiscountCode ?? undefined,
        contact: buildContactPayload(form),
        uiMode: requestedUiMode,
        successUrl: `${baseUrl}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${baseUrl}/checkout/cancel`,
      };
      const response = await apiFetch<CreateCheckoutSessionResponse>(
        '/api/funnel/checkout/session',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );

      if (response.clientSecret) {
        setCheckoutSessionId(response.checkoutSessionId);
        setCheckoutClientSecret(response.clientSecret);
        setSubmitting(false);
        return;
      }

      if (response.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
        return;
      }

      throw new Error('Unable to initialize Stripe Checkout.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment init failed.');
      setSubmitting(false);
    }
  }, [
    appliedDiscountCode,
    canCheckout,
    contactComplete,
    discountCodeInput,
    form,
    items,
    leadCaptureId,
    resetCustomCheckout,
  ]);

  const confirmCustomPayment = useCallback(async () => {
    if (!checkoutConfirmRef.current) {
      setError('Payment form is still loading. Please wait a moment.');
      return;
    }
    if (!checkoutSessionId) {
      setError('Missing checkout session. Please restart billing.');
      return;
    }

    setConfirmingPayment(true);
    setError(null);
    try {
      const result = await checkoutConfirmRef.current({
        redirect: 'if_required',
      });

      if (result?.type === 'error') {
        throw new Error(result.error?.message || 'Payment confirmation failed.');
      }

      // Success page handles polling/order-finalization status via session_id.
      navigate(`/checkout/success?session_id=${encodeURIComponent(checkoutSessionId)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment confirmation failed.');
      setConfirmingPayment(false);
    }
  }, [checkoutSessionId, navigate]);

  if (!canCheckout) {
    return (
      <FunnelShell>
        <MainColumn>
          <Heading>Checkout</Heading>
          <p style={{ margin: 0 }}>
            Your cart is empty. Select a service tier before checkout.
          </p>
          <Actions>
            <PrimaryButton type="button" onClick={() => navigate('/services')}>
              Go to services
            </PrimaryButton>
          </Actions>
        </MainColumn>
      </FunnelShell>
    );
  }

  return (
    <FunnelShell>
      <Layout>
        <MainColumn>
          <Heading>Checkout</Heading>
          <Actions>
            <SubtleActionButton type="button" onClick={continueShopping}>
              Continue shopping
            </SubtleActionButton>
          </Actions>
          {recoveringCheckout ? (
            <BillingNote>Restoring your saved checkout...</BillingNote>
          ) : null}
          {sharedCheckoutSummary ? (
            <InlineSummary>
              <span>Shared configuration loaded</span>
              <span>
                {sharedCheckoutSummary.serviceName} - {sharedCheckoutSummary.tierName}
              </span>
              {sharedCheckoutSummary.laneLabels.length ? (
                <span>Lanes: {sharedCheckoutSummary.laneLabels.join(', ')}</span>
              ) : null}
              {sharedCheckoutSummary.addOnLabels.length ? (
                <span>Add-ons: {sharedCheckoutSummary.addOnLabels.join(', ')}</span>
              ) : null}
            </InlineSummary>
          ) : null}

          <Accordion>
            <AccordionHeader>
                <AccordionTitleBlock>
                  <AccordionTitle>Contact</AccordionTitle>
                  <AccordionDescription>
                    {!showContactForm && form.bio.trim()
                      ? form.bio.trim()
                      : 'Tell us a little about yourself.'}
                  </AccordionDescription>
                </AccordionTitleBlock>
              {contactComplete && !showContactForm ? (
                <EditButton
                  type="button"
                  onClick={() => {
                    setShowContactForm(true);
                    resetCustomCheckout();
                    setError(null);
                  }}
                >
                  Edit
                </EditButton>
              ) : null}
            </AccordionHeader>

            {showContactForm ? (
              <>
                <ContactForm>
                  <ContactField>
                    Email*
                    <ContactInput
                      type="email"
                      placeholder="name@company.com"
                      value={form.email}
                      $invalid={Boolean(emailMismatchError)}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, email: event.target.value }))
                      }
                    />
                  </ContactField>
                  <ContactField>
                    Confirm email*
                    <ContactInput
                      type="email"
                      placeholder="Re-enter email"
                      value={form.confirmEmail}
                      $invalid={Boolean(emailMismatchError)}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, confirmEmail: event.target.value }))
                      }
                    />
                    {emailMismatchError ? (
                      <ContactFieldError>{emailMismatchError}</ContactFieldError>
                    ) : null}
                  </ContactField>
                  <ContactRow>
                    <ContactField>
                      First name*
                      <ContactInput
                        placeholder="First name"
                        value={form.firstName}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, firstName: event.target.value }))
                        }
                      />
                    </ContactField>
                    <ContactField>
                      Last name*
                      <ContactInput
                        placeholder="Last name"
                        value={form.lastName}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, lastName: event.target.value }))
                        }
                      />
                    </ContactField>
                    <ContactField>
                      Phone number
                      <ContactInput
                        type="tel"
                        placeholder="Phone number"
                        value={form.phone}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, phone: event.target.value }))
                        }
                      />
                    </ContactField>
                  </ContactRow>
                  <ContactField>
                    Tell us about yourself
                    <ContactTextArea
                      placeholder="Tell us a little about yourself, your business, or what you need help with..."
                      value={form.bio}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, bio: event.target.value }))
                      }
                    />
                  </ContactField>
                </ContactForm>
                <Actions>
                  <PrimaryButton
                    type="button"
                    onClick={() => void saveContactAndContinue()}
                    disabled={
                      capturingLead ||
                      submitting ||
                      recoveringCheckout ||
                      Boolean(contactError)
                    }
                  >
                    {capturingLead ? 'Saving info...' : 'Continue'}
                  </PrimaryButton>
                </Actions>
              </>
            ) : (
              <InlineSummary>
                <span>
                  {form.firstName.trim()} {form.lastName.trim()}
                </span>
                <span>{form.email.trim()}</span>
                {form.phone.trim() ? <span>{form.phone.trim()}</span> : null}
              </InlineSummary>
            )}
          </Accordion>

          <AnimatedSection $visible={contactComplete && !showContactForm}>
            <Accordion>
              <AccordionHeader>
                <AccordionTitleBlock>
                  <AccordionTitle>Review + Billing</AccordionTitle>
                  <AccordionDescription>
                    Confirm your pricing, then continue to secure Stripe payment.
                  </AccordionDescription>
                </AccordionTitleBlock>
              </AccordionHeader>

              <DiscountRow>
                <ContactField>Discount code</ContactField>
                <DiscountInputRow>
                  <DiscountInput
                    value={discountCodeInput}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setDiscountCodeInput(nextValue);
                      resetCustomCheckout();
                      if (
                        appliedDiscountCode &&
                        nextValue.trim().toUpperCase() !== appliedDiscountCode
                      ) {
                        setAppliedDiscountCode(null);
                        setQuotedOrder(null);
                      }
                    }}
                    placeholder="Enter code"
                  />
                  <PrimaryButton
                    type="button"
                    onClick={() => void applyDiscountCode()}
                    disabled={applyingDiscount || submitting}
                  >
                    {applyingDiscount ? 'Applying...' : 'Apply discount'}
                  </PrimaryButton>
                  {appliedDiscountCode ? (
                    <GhostButton
                      type="button"
                      onClick={clearDiscountCode}
                      disabled={applyingDiscount || submitting}
                    >
                      Remove code
                    </GhostButton>
                  ) : null}
                </DiscountInputRow>
                {appliedDiscountCode ? (
                  <DiscountMeta>Applied code: {appliedDiscountCode}</DiscountMeta>
                ) : null}
              </DiscountRow>

              <OrderBreakdown order={displayOrder} flat onRemoveSku={removeItemFromCheckout} />

              <BillingNote>
                Confirm totals, then start payment.
              </BillingNote>

              {checkoutClientSecret ? (
                <>
                  <PaymentSection>
                    <PaymentMount ref={checkoutMountRef} />
                  </PaymentSection>
                  <Actions>
                    <PrimaryButton
                      type="button"
                      onClick={() => void confirmCustomPayment()}
                      disabled={!checkoutActionsReady || confirmingPayment || recoveringCheckout}
                    >
                      {confirmingPayment
                        ? 'Confirming payment...'
                        : checkoutActionsReady
                          ? 'Complete purchase'
                          : 'Loading payment form...'}
                    </PrimaryButton>
                  </Actions>
                </>
              ) : (
                <Actions>
                  <PrimaryButton
                    type="button"
                    onClick={() => void beginPayment()}
                    disabled={submitting || confirmingPayment || recoveringCheckout}
                  >
                    {submitting ? 'Starting checkout...' : 'Enter billing & buy'}
                  </PrimaryButton>
                </Actions>
              )}
            </Accordion>
          </AnimatedSection>

          {error ? <ErrorText role="alert">{error}</ErrorText> : null}
        </MainColumn>

        <SideColumn $visible={!showContactForm}>
          <OrderBreakdown order={displayOrder} hidePrices={!contactComplete} flat />
          <HelpBox flat />
        </SideColumn>
      </Layout>
    </FunnelShell>
  );
}

